import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { delay } from '@home/utils/bubbleHelpers';

// 动画时间常量
const BLINK_DURATION = 240; // 眨眼持续时间（ms）
const BLINK_INTERVAL = 200; // 两次眨眼间隔（ms）
const BUBBLE_FIRST_BLINK_DELAY = 500; // 气泡出现后首次眨眼延迟（ms）
const BUBBLE_SECOND_BLINK_DELAY = 4900; // 气泡出现后第二次眨眼延迟（ms）
const IDLE_DETECTION_TIMEOUT = 60000; // 闲置检测超时时间（ms）
const ACTIVITY_DEBOUNCE_DELAY = 1000; // 活动检测防抖延迟（ms）

interface IUseBlinkAnimationReturn {
  isBlinking: boolean;
}

/**
 * 眨眼动画 Hook
 * 处理 Robot 图标的眨眼动画逻辑：
 * 1. 气泡出现时触发（0.5s 和 5s 各一次）
 * 2. 用户闲置 60s 无交互时触发（循环）
 */
export const useBlinkAnimation = (showBubble: boolean): IUseBlinkAnimationReturn => {
  const [isBlinking, setIsBlinking] = useState(false);
  const isAnimatingRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isActiveRef = useRef(true);

  // 执行两次眨眼（单次眨眼 = 闭眼240ms，两次之间间隔200ms）
  const blinkTwice = useCallback(async () => {
    if (isAnimatingRef.current) return;

    try {
      isAnimatingRef.current = true;

      setIsBlinking(true);
      await delay(BLINK_DURATION);
      setIsBlinking(false);
      await delay(BLINK_INTERVAL);
      setIsBlinking(true);
      await delay(BLINK_DURATION);
      setIsBlinking(false);
    } catch (error) {
      console.error('[AgentRobot] Blink animation error:', error);
      setIsBlinking(false);
    } finally {
      isAnimatingRef.current = false;
    }
  }, []);

  // 气泡出现后定时触发眨眼动画（0.5s 和 5s 各触发一次）
  useEffect(() => {
    if (!showBubble) return;

    const timers = [
      setTimeout(() => void blinkTwice(), BUBBLE_FIRST_BLINK_DELAY),
      setTimeout(() => void blinkTwice(), BUBBLE_SECOND_BLINK_DELAY),
    ];

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [showBubble, blinkTwice]);

  // 用户停滞检测——超过60s无任何交互（鼠标移动/点击/滚动/输入）则眨眼2次，循环
  useEffect(() => {
    isActiveRef.current = true;

    const scheduleIdleBlink = () => {
      if (!isActiveRef.current) return;

      idleTimerRef.current = setTimeout(() => {
        // 在回调内部再次检查 isActive，确保组件未卸载
        if (!isActiveRef.current) return;
        
        void blinkTwice();
        scheduleIdleBlink();
      }, IDLE_DETECTION_TIMEOUT);
    };

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (isActiveRef.current) scheduleIdleBlink();
    };

    // 使用防抖优化高频事件，1秒内只触发一次
    const debouncedResetIdleTimer = debounce(resetIdleTimer, ACTIVITY_DEBOUNCE_DELAY);

    scheduleIdleBlink();

    const events = ['mousemove', 'click', 'scroll', 'keydown'] as const;
    events.forEach(e => window.addEventListener(e, debouncedResetIdleTimer, { passive: true }));

    return () => {
      isActiveRef.current = false;
      debouncedResetIdleTimer.cancel(); // 取消防抖函数，防止内存泄漏
      events.forEach(e => window.removeEventListener(e, debouncedResetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [blinkTwice]);

  return { isBlinking };
};
